/**
 * analysisParameterHelpers.js
 *
 * 「分析參數」是分析服務品項在步驟四要額外填的分析設定（不是樣本資料，也不是物種），
 * 會一併寫進分析需求單。這裡刻意做成一份 registry，之後要加參數或套到別的品項時
 * 只要往 ANALYSIS_PARAMETER_DEFINITIONS 加一筆，UI、驗證、送出與預覽都會自動跟上。
 *
 * 目前只有 ChIP-seq（A-B035 / A-B036 / A-B037 / A-B038）用到。
 *
 * 每筆定義：
 * - id           送出的欄位名，也是 analysisParameters 物件的 key
 * - label        畫面上的欄位名稱
 * - type         'select' 或 'number'
 * - options      select 用：{ value, label, description, group, suggests }
 *                description 會顯示在說明區；group 是選單裡的分組標題；
 *                suggests 是「選了這個值，通常建議另一個參數用什麼」（例如抗體 → peak calling mode）
 * - allowCustom  select 用：多一個「其他（自填）」選項，存使用者自填的文字
 * - describeInOption  select 用：選項說明直接寫在選單文字裡，不在欄位下方另外顯示
 * - required     true 時沒有預設值，使用者一定要選（例如抗體）
 * - defaultValue 預設值；select 必須是 options 之一，number 是數字
 * - min / max / minExclusive  number 的合法範圍
 * - advanced     true 時收在「進階閾值（通常不用改）」裡，預設收合
 * - hint         欄位下方的小字說明
 * - target       分析需求單的落點，後端 ANALYSIS_PARAMETER_TARGETS 會對照同一組 id
 */

import { extractAnalysisServiceCode } from './scRnaExperimentHelpers';

/** 「其他（自填）」在下拉選單裡的值；真正送出的是使用者填的文字，不會是這個值。 */
export const ANALYSIS_PARAMETER_CUSTOM_OPTION_VALUE = '__custom__';

const PEAK_CALLING_MODE = {
  id: 'peakCallingMode',
  label: 'Peak calling mode',
  type: 'select',
  defaultValue: 'narrow',
  hint: '此參數供 MACS3 peak calling 使用，依抗體所抓的訊號型態選擇。',
  options: [
    {
      value: 'narrow',
      label: 'narrow',
      description: '適用於轉錄因子（Transcription Factors, TFs）以及多數窄的 histone modifications（如 H3K4me3、H3K27ac）。'
        + '特徵是結合位點非常集中，呈尖銳的峰（Sharp peaks）。'
    },
    {
      value: 'broad',
      label: 'broad',
      description: '適用於寬闊的組蛋白修飾（Broad/diffuse histone marks），例如 H3K27me3、H3K36me3、H3K9me3。'
        + '特徵是訊號在染色體上分佈較廣、覆蓋區域較大（寬峰）。'
    }
  ],
  target: { sheet: 'ChIP-seq', cell: 'F122' }
};

const NARROW_ANTIBODY_GROUP = '訊號集中（通常用 narrow）';
const BROAD_ANTIBODY_GROUP = '訊號分佈較寬（通常用 broad）';

const antibodyOption = (value, description, peakCallingMode) => ({
  value,
  label: value,
  description,
  group: peakCallingMode === 'broad' ? BROAD_ANTIBODY_GROUP : NARROW_ANTIBODY_GROUP,
  suggests: { peakCallingMode }
});

const ANTIBODY = {
  id: 'antibody',
  label: 'Antibody（抗體）',
  type: 'select',
  allowCustom: true,
  customOptionLabel: '其他（自填）',
  customPlaceholder: '請填抗體名稱，例如 H3K79me2、FOXA1',
  customHint: '選單沒有的抗體請直接填名稱。轉錄因子一般選 narrow；'
    + '訊號分佈很寬的組蛋白修飾選 broad，不確定時可參考下方 peak calling mode 的說明，或向客戶確認。',
  // 抗體沒有預設值：模板寫死的 H3K27ac 不能當成客戶的選擇。
  required: true,
  defaultValue: '',
  maxLength: 100,
  hint: '請向客戶確認這批 ChIP 使用的抗體（target）。',
  suggestionSourceLabel: '抗體',
  // 選項的白話說明直接寫在選單文字裡（H3K27ac — 活化的 enhancer 與 promoter），不另外佔一行。
  describeInOption: true,
  options: [
    antibodyOption('H3K27ac', '活化的 enhancer 與 promoter', 'narrow'),
    antibodyOption('H3K4me3', '活化的 promoter', 'narrow'),
    antibodyOption('H3K9ac', '活化的 promoter', 'narrow'),
    antibodyOption('CTCF', '染色質結構蛋白，轉錄因子類', 'narrow'),
    antibodyOption('RNA Pol II', '轉錄機器', 'narrow'),
    antibodyOption('H3K4me1', 'enhancer，含尚未活化的', 'broad'),
    antibodyOption('H3K27me3', 'Polycomb 抑制區域', 'broad'),
    antibodyOption('H3K36me3', '正在轉錄的基因本體', 'broad'),
    antibodyOption('H3K9me3', '異染色質', 'broad')
  ],
  target: { sheet: 'ChIP-seq', cell: 'F123' }
};

const REPRODUCIBILITY_CRITERIA = {
  id: 'reproducibilityCriteria',
  label: 'Reproducibility criteria',
  type: 'number',
  defaultValue: 0.67,
  min: 0,
  minExclusive: true,
  max: 1,
  step: 0.01,
  rangeText: '需大於 0 且不超過 1',
  advanced: true,
  hint: '同一組重複樣本中，一個 peak 至少要在多少比例的樣本出現，才會納入該組的 consensus peak；'
    + '0.67 約等於 3 個重複中至少 2 個。',
  target: { sheet: 'ChIP-seq', cell: 'F121' }
};

const LOG2_FOLD_CHANGE = {
  id: 'log2FoldChange',
  label: '|Log2FoldChange|',
  type: 'number',
  defaultValue: 1,
  min: 0,
  minExclusive: true,
  step: 0.1,
  rangeText: '需大於 0',
  advanced: true,
  hint: '差異分析（DESeq2）判定差異 peak 的倍數門檻，1 代表兩組相差至少 2 倍。',
  target: { sheet: 'ChIP-seq', cell: 'F119' }
};

const P_ADJUST = {
  id: 'pAdjust',
  label: 'p-adjust',
  type: 'number',
  defaultValue: 0.05,
  min: 0,
  minExclusive: true,
  max: 1,
  step: 0.01,
  rangeText: '需大於 0 且不超過 1',
  advanced: true,
  hint: '校正後的 p 值門檻，越小越嚴格。',
  target: { sheet: 'ChIP-seq', cell: 'F120' }
};

/** Basic 系列（Basic / Customized_1）沒有差異分析，所以只有 peak 相關的參數。 */
const CHIPSEQ_BASIC_PARAMETERS = [ANTIBODY, PEAK_CALLING_MODE, REPRODUCIBILITY_CRITERIA];

/** Advanced 系列（Advanced / Customized_2）多了 DESeq2，要再填差異分析的兩個閾值。 */
const CHIPSEQ_ADVANCED_PARAMETERS = [...CHIPSEQ_BASIC_PARAMETERS, LOG2_FOLD_CHANGE, P_ADJUST];

/** 品項代號 → 該品項要填的分析參數。沒列到的品項就不顯示「分析參數」區塊。 */
const ANALYSIS_PARAMETER_DEFINITIONS = {
  'A-B035': CHIPSEQ_BASIC_PARAMETERS,
  'A-B036': CHIPSEQ_ADVANCED_PARAMETERS,
  'A-B037': CHIPSEQ_BASIC_PARAMETERS,
  'A-B038': CHIPSEQ_ADVANCED_PARAMETERS
};

const normalizeServiceCatalog = (serviceCatalog = {}) => {
  if (!serviceCatalog || typeof serviceCatalog !== 'object') return [];

  return Object.values(serviceCatalog).flatMap(category => (
    Array.isArray(category) ? category : []
  ));
};

const getSelectedServiceValues = (serviceItems = []) => (
  (Array.isArray(serviceItems) ? serviceItems : [])
    .flatMap(item => (Array.isArray(item?.services) ? item.services : []))
    .map(service => service?.service)
    .filter(Boolean)
);

/**
 * 這張單要填哪些分析參數。同一個參數被多個品項共用時只會出現一次。
 * serviceCatalog 目前用不到，保留是為了之後改成看設定檔旗標時不用改呼叫端。
 */
export const getAnalysisParameterDefinitions = ({
  serviceItems = [],
  serviceCatalog = {}
} = {}) => {
  const seen = new Set();
  const definitions = [];

  getSelectedServiceValues(serviceItems).forEach(serviceValue => {
    const serviceDefinitions = ANALYSIS_PARAMETER_DEFINITIONS[extractAnalysisServiceCode(serviceValue)];
    if (!serviceDefinitions) return;

    serviceDefinitions.forEach(definition => {
      if (seen.has(definition.id)) return;

      seen.add(definition.id);
      definitions.push(definition);
    });
  });

  // serviceCatalog 目前只用來確認品項存在，缺設定檔時仍以代號為準。
  void normalizeServiceCatalog(serviceCatalog);

  return definitions;
};

export const hasAnalysisParameters = (args = {}) => getAnalysisParameterDefinitions(args).length > 0;

export const getAnalysisParameterDefinitionById = (definitions = [], id = '') => (
  (Array.isArray(definitions) ? definitions : []).find(definition => definition.id === id) || null
);

const isAllowedValue = (definition, value) => (
  (definition?.options || []).some(option => option.value === value)
);

const findOption = (definition, value) => (
  (definition?.options || []).find(option => option.value === value) || null
);

/** 自填文字：去掉控制字元、收斂空白、限制長度。 */
const sanitizeCustomText = (definition, value) => (
  Array.from(String(value ?? ''), char => (char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127 ? ' ' : char))
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, definition?.maxLength || 100)
    .trim()
);

/**
 * 可以自填的下拉選單：選單裡有的值（大小寫不同也算）統一成選單的寫法，
 * 其他就當成自填文字。
 */
const normalizeCustomSelectValue = (definition, value) => {
  const text = sanitizeCustomText(definition, value);
  if (!text) return '';

  const option = (definition.options || []).find(item => item.value.toLowerCase() === text.toLowerCase());
  return option ? option.value : text;
};

/**
 * 數字參數：接受數字或數字字串（輸入框打的是字串），不在範圍內或不是數字回傳 null。
 */
export const parseAnalysisParameterNumber = (definition, value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return null;

  const text = String(value).trim();
  if (!text || !/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(text)) return null;

  const number = Number(text);
  if (!Number.isFinite(number)) return null;
  if (typeof definition?.min === 'number') {
    if (definition.minExclusive ? number <= definition.min : number < definition.min) return null;
  }
  if (typeof definition?.max === 'number' && number > definition.max) return null;

  return number;
};

/**
 * 單一參數的驗證錯誤（送出前用）。沒有錯誤回傳空字串。
 * 數字參數吃的是輸入框的字串，所以空白、非數字、超出範圍都要擋。
 */
export const getAnalysisParameterError = (definition, value) => {
  if (!definition) return '';

  if (definition.type === 'number') {
    const text = String(value ?? '').trim();
    if (!text) return `請填寫 ${definition.label}`;
    if (!Number.isFinite(Number(text))) return `${definition.label} 必須是數字`;
    if (parseAnalysisParameterNumber(definition, text) === null) {
      return `${definition.label} ${definition.rangeText || '超出允許範圍'}`;
    }
    return '';
  }

  if (definition.allowCustom) {
    if (definition.required && !sanitizeCustomText(definition, value)) {
      return `請選擇或填寫 ${definition.label}`;
    }
    return '';
  }

  if (definition.required && !isAllowedValue(definition, value)) {
    return `請選擇 ${definition.label}`;
  }

  return '';
};

/** 整張單的分析參數錯誤：{ 參數 id: 錯誤訊息 }，沒有錯誤時為空物件。 */
export const getAnalysisParameterErrors = (definitions = [], values = {}) => {
  const source = values && typeof values === 'object' ? values : {};

  return (Array.isArray(definitions) ? definitions : []).reduce((errors, definition) => {
    const error = getAnalysisParameterError(definition, source[definition.id]);
    if (error) errors[definition.id] = error;
    return errors;
  }, {});
};

/**
 * 把表單上的值整理成「只留下目前品項需要、而且值合法」的物件（送出與還原舊單都用這個）。
 * - 換品項後不再適用的參數會被丟掉，避免像實驗類型那樣殘留在送出的資料裡
 * - 數字一律轉成 JSON number；不合法（舊單、手改的 JSON）退回預設值
 * - 下拉選單的值不在選單內退回預設值；可自填的（抗體）保留整理過的文字，沒填就是空字串
 * 這裡不會因為抗體去改 peak calling mode：建議值只在使用者親手換抗體時套用。
 */
export const normalizeAnalysisParameters = (definitions = [], values = {}) => {
  const source = values && typeof values === 'object' ? values : {};

  return (Array.isArray(definitions) ? definitions : []).reduce((result, definition) => {
    const value = source[definition.id];

    if (definition.type === 'number') {
      const number = parseAnalysisParameterNumber(definition, value);
      result[definition.id] = number === null ? definition.defaultValue : number;
    } else if (definition.allowCustom) {
      result[definition.id] = normalizeCustomSelectValue(definition, value);
    } else {
      result[definition.id] = isAllowedValue(definition, value) ? value : definition.defaultValue;
    }

    return result;
  }, {});
};

export const getDefaultAnalysisParameters = (definitions = []) => normalizeAnalysisParameters(definitions, {});

/**
 * 畫面用的值：一樣只留目前品項的參數，但數字保留使用者正在打的字串（例如「0.」），
 * 不合法的值留著讓送出時的驗證擋下來並提示，而不是偷偷改掉。
 */
export const resolveAnalysisParameterFormValues = (definitions = [], values = {}) => {
  const source = values && typeof values === 'object' ? values : {};

  return (Array.isArray(definitions) ? definitions : []).reduce((result, definition) => {
    const value = source[definition.id];

    if (definition.type === 'number') {
      result[definition.id] = value === null || value === undefined ? String(definition.defaultValue) : String(value);
    } else if (definition.allowCustom) {
      result[definition.id] = value === null || value === undefined ? '' : String(value);
    } else {
      result[definition.id] = isAllowedValue(definition, value) ? value : definition.defaultValue;
    }

    return result;
  }, {});
};

/**
 * 可自填的下拉選單現在該顯示哪個選項：
 * 選單裡有的值 → 該選項；有填但不在選單內（含還原的舊單）→「其他」；沒填 → 請選擇。
 * customMode 是使用者剛選了「其他」但還沒打字的狀態，要維持在「其他」。
 */
export const getAnalysisParameterSelectValue = (definition, value, { customMode = false } = {}) => {
  if (!definition?.allowCustom) return value;
  if (customMode) return ANALYSIS_PARAMETER_CUSTOM_OPTION_VALUE;

  const text = String(value ?? '').trim();
  if (!text) return '';
  return isAllowedValue(definition, text) ? text : ANALYSIS_PARAMETER_CUSTOM_OPTION_VALUE;
};

/**
 * 還原舊單 / 草稿時，哪些可自填的參數一開始就要停在「其他」：有值但不在選單內（大小寫不同不算）。
 * 不先標起來的話，使用者把還原回來的自填文字刪光重打，選單會跳回「請選擇」、輸入框跟著消失。
 */
export const deriveAnalysisParameterCustomModes = (definitions = [], values = {}) => {
  const source = values && typeof values === 'object' ? values : {};

  return (Array.isArray(definitions) ? definitions : []).reduce((modes, definition) => {
    if (!definition?.allowCustom) return modes;

    const text = normalizeCustomSelectValue(definition, source[definition.id]);
    if (text && !isAllowedValue(definition, text)) modes[definition.id] = true;
    return modes;
  }, {});
};

/**
 * 使用者親手改了一個參數之後的新值。選了有建議值的選項（例如抗體 H3K27me3 → broad）時，
 * 會一併把被建議的參數改掉。只有使用者操作才呼叫這個；還原舊單或草稿時不會走到這裡，
 * 所以不會蓋掉當初存下來的設定。
 * 在「其他」輸入框打字時要傳 applySuggestion: false：打到剛好跟選單同名（例如 CTCF）
 * 不該偷偷改 peak calling mode，建議值只在使用者從選單挑抗體時才套用。
 */
export const applyAnalysisParameterChange = (
  definitions = [],
  values = {},
  id = '',
  value = '',
  { applySuggestion = true } = {}
) => {
  const next = { ...(values && typeof values === 'object' ? values : {}), [id]: value };
  if (!applySuggestion) return next;

  const definition = getAnalysisParameterDefinitionById(definitions, id);
  const suggests = findOption(definition, value)?.suggests || {};

  Object.entries(suggests).forEach(([targetId, suggestedValue]) => {
    const targetDefinition = getAnalysisParameterDefinitionById(definitions, targetId);
    if (targetDefinition && isAllowedValue(targetDefinition, suggestedValue)) {
      next[targetId] = suggestedValue;
    }
  });

  return next;
};

/**
 * 目前的選擇跟建議不一樣時的提示（不擋送出）：{ 被建議的參數 id: 提示文字 }。
 * 例如抗體選 H3K27me3、peak calling mode 卻是 narrow →「依抗體 H3K27me3，通常建議用 broad」。
 */
export const getAnalysisParameterSuggestionWarnings = (definitions = [], values = {}) => {
  const source = values && typeof values === 'object' ? values : {};
  const warnings = {};

  (Array.isArray(definitions) ? definitions : []).forEach(definition => {
    const option = findOption(definition, source[definition.id]);
    if (!option?.suggests) return;

    Object.entries(option.suggests).forEach(([targetId, suggestedValue]) => {
      const targetDefinition = getAnalysisParameterDefinitionById(definitions, targetId);
      if (!targetDefinition || source[targetId] === suggestedValue) return;

      const suggestedLabel = findOption(targetDefinition, suggestedValue)?.label || suggestedValue;
      warnings[targetId] = `依${definition.suggestionSourceLabel || definition.label} ${option.label}，`
        + `通常建議用 ${suggestedLabel}`;
    });
  });

  return warnings;
};

/** 預覽與摘要用：把參數整理成 { id, label, value, valueLabel }。 */
export const buildAnalysisParameterSummary = (definitions = [], values = {}) => {
  const normalized = normalizeAnalysisParameters(definitions, values);

  return (Array.isArray(definitions) ? definitions : []).map(definition => {
    const value = normalized[definition.id];
    const option = findOption(definition, value);

    let valueLabel = option?.label || (value === '' ? '' : String(value));
    if (!valueLabel && definition.required) valueLabel = '（未填）';

    return {
      id: definition.id,
      label: definition.label,
      value,
      valueLabel
    };
  });
};
